import React from "react";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { useAuth } from "./AuthContext";
import Footer from "./Footer";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  return (
    <>
      <div className="w-full flex justify-center text-[#eee] bg-[#111]">
        <div className="w-full max-w-[1100px] px-6 md:px-4">
          {/* Header */}
          <div className="w-full flex flex-row items-center justify-between mt-6">
            <div
              onClick={() => navigate("/")}
              className="flex flex-row items-center mb-6 gap-x-2 cursor-pointer"
            >
              <h1 className="text-3xl font-bold font-bebas tracking-wider">
                PLAYSET
              </h1>
            </div>

            <button
              onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
              className="px-4 py-2 bg-yellow-500/20 border border-yellow-500 text-white rounded-xl cursor-pointer hover:bg-yellow-500 transition"
            >
              {currentUser ? "Dashboard" : "Sign In"}
            </button>
          </div>

          {/* Title */}
          <div className="font-bebas text-4xl text-[#fff] font-bold leading-snug tracking-wider leading-[5rem] md:leading-none">
            Privacy Policy
          </div>

          <div className="font-opensans space-y-10 mb-10">
            <p className="my-6 opacity-70">
              <strong>Last Updated:</strong> 11th January 2026
            </p>

            {/* Intro */}
            <div className="space-y-2">
              <p>
                Playset (
                <a className="underline" href="https://playset.app">
                  https://playset.app
                </a>
                ) is committed to protecting your privacy. This privacy policy
                explains how we collect, use, and protect your personal information.
              </p>
            </div>

            {/* Your Personal Data Section */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Your Personal Data</h3>
              <p>
                Personal data is only stored once you create a Playset account,
                which happens when you sign in for the first time.
              </p>
              <p>Playset stores the following personal information about you:</p>
              <ul className="list-disc pl-8 space-y-2">
                <li>Email address</li>
                <li>Profile information (name, avatar)</li>
                <li>Game projects you create and manage</li>
                <li>Playtest session recordings and metrics</li>
              </ul>
            </div>

            {/* Cookies & Authentication Section */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Cookies & Authentication</h3>
              <p>
                We comply with GDPR and only use authentication tokens and
                cookies that are strictly necessary to provide our core services:
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>
                  <strong>Supabase Authentication:</strong> Required for secure
                  user login and account management.
                </li>
                <li>
                  <strong>Stripe:</strong> Our payment processor, used when you
                  upgrade your subscription or purchase credits.
                </li>
                <li>
                  <strong>Session Storage:</strong> We store encrypted session
                  data necessary for authentication and platform functionality.
                </li>
              </ul>
            </div>

            {/* Data Collection & Playtest Information */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Data Collection & Playtest Information</h3>
              <p>
                When you upload game builds and conduct playtests on Playset, we
                collect and process the following information:
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>Game build files and metadata</li>
                <li>Playtest session recordings (video/audio when enabled)</li>
                <li>Tester feedback and comments</li>
                <li>Gameplay analytics and performance metrics</li>
                <li>Device and browser information from testers</li>
              </ul>
              <p className="mt-2">
                This data is stored securely on our servers and is only accessible
                to you and the testers you invite. You maintain full control over
                who can access your playtest data.
              </p>
            </div>

            {/* Third-Party Services */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Third-Party Services</h3>
              <p>
                Playset uses the following third-party services to provide our
                platform:
              </p>
              <ul className="list-disc pl-8 space-y-2">
                <li>
                  <strong>Supabase:</strong> Database and authentication hosting
                </li>
                <li>
                  <strong>Stripe:</strong> Payment processing and billing
                </li>
                <li>
                  <strong>Cloudflare R2:</strong> Secure storage for game builds
                  and session recordings
                </li>
              </ul>
              <p className="mt-2">
                By using Playset, you consent to these services processing your
                data as necessary to provide the platform. Please review their
                respective privacy policies for more details.
              </p>
            </div>

            {/* Data Security */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Data Security</h3>
              <p>
                We use industry-standard encryption and security practices to
                protect your data. All data transmitted between your device and
                our servers is encrypted using SSL/TLS. Game builds and session
                recordings are stored securely and access is restricted to
                authorized users.
              </p>
            </div>

            {/* Contact Us */}
            <div className="space-y-2">
              <h3 className="font-bebas text-2xl">Contact Us</h3>
              <p>
                If you have any questions about this privacy policy or how we
                handle your data, please contact us from the{" "}
                <a className="underline" href="/support">
                  Support
                </a>{" "}
                page.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}
